/**
 * @module ConstructorIO/search
 */

import ConstructorIOClient from '@constructor-io/constructorio-client-javascript';
import {
    processCategories,
    processRefinements,
    processSortingOptions,
    processProductSearchResults
} from '../helpers/result';

import {getCountryPath} from '../../spa/helper';
import ConstructorRefinementURLBuilder from './refinement-url-builder';

/**
 *  Class wrapping the Constructor.io search and category navigation functionalities.
 */
class ConstructorIOSearch {
    #country = null;

    #filters = null;

    #client = null;

    #resultsPerPage = null;

    #currentPage = null;

    #nextPage = null;

    #pages = null;

    #sortBy = null;

    #sortOrder = null;

    #variations_map = null;

    isEnabled = false;

    /**
     * @type {import('../../spa/type-definitions').SearchResult}
     */
    #emptyResult = {
        products: null,
        sortingOptions: null,
        refinements: null,
        categories: null,
        numTotalResults: 0,
        page: 0,
        nextPage: 0,
        pages: 0,
        isCategorySearch: false,
        refinementURLBuilder: null
    };

    constructor() {
        this.isEnabled =
            window.CONSTRUCTOR_SEARCH_ENABLED && window.IS_CONSTRUCTOR_RESULT && window.CONSTRUCTOR_API_KEY !== null;
        if (!this.isEnabled) {
            return;
        }

        this.#client = new ConstructorIOClient({
            apiKey: window.CONSTRUCTOR_API_KEY,
            userId: window.customer.customerNo
        });
        this.#country = window.CURRENT_COUNTRY;
        this.#setParameters();
    }

    /**
     * @param {Object} [parameters] - search parameters
     */
    #setParameters(parameters) {
        const {searchParams} = new URL(window.location.href);
        const pageSize = window.SitePreferences.DEFAULT_PAGE_SIZE;

        this.#resultsPerPage =
            pageSize <= window.SitePreferences.MAX_PAGE_SIZE ? pageSize : window.SitePreferences.MAX_PAGE_SIZE;

        const pageParam = parseInt(searchParams.get('page'));

        if (parameters?.options?.nonInstantUpdate || Number.isNaN(pageParam) || pageParam < 1) {
            this.#currentPage = 1;
            this.#nextPage = 1;
        } else {
            this.#currentPage = parseInt(searchParams.get('page'));
            this.#nextPage = parseInt(searchParams.get('page'));
        }

        const filters = {};

        filters[`available_${window.Constants.SITE_ID}`] = 'True';
        if (parameters?.options?.nonInstantUpdate !== true) {
            searchParams.forEach((value, key) => {
                if (key.includes('filter')) {
                    const filter = key.match(/\[(.*?)\]/)?.[1]; // extract the filter name from parameter e.g. filters[sizeName_GB]
                    if (filter) {
                        const splitValue = value.split(',');
                        filters[filter] = splitValue;
                    }
                } else if (key === 'sort_by') {
                    this.#sortBy = value;
                } else if (key === 'sort_order') {
                    this.#sortOrder = value;
                }
            });
        }

        if (parameters?.filters) {
            for (const [id, value] of Object.entries(parameters.filters)) {
                if (filters[id]) {
                    let values = filters[id];
                    if (typeof values === 'string') {
                        values = values.split(',');
                    }
                    filters[id] = [...values, ...value.split(',')];
                } else {
                    let splitValue = value;
                    if (typeof value === 'string') {
                        splitValue = value.split(',');
                    }
                    filters[id] = splitValue;
                }
            }
        }

        this.#variations_map = {
            values: {variation_ids: {aggregation: 'all', field: 'data.variation_id'}},
            dtype: 'array'
        };

        this.#filters = filters;
    }

    /**
     * @param {Object} results - Constructor results
     * @returns {import('../../spa/type-definitions').SearchResult} result object
     */
    #handleResults = results => {
        const {response, request} = results;

        if (response) {
            if (response.results && response.results.length !== 0) {
                const products = processProductSearchResults(response.results);
                const sortingOptions = processSortingOptions(response.sort_options);
                const refinementsData = processRefinements(response.facets);
                const categories = processCategories(response.groups);
                this.#currentPage = request.page;
                this.#pages = Math.ceil(response.total_num_results / this.#resultsPerPage);
                this.#nextPage = this.#currentPage + 1 <= this.#pages ? this.#currentPage + 1 : null;

                return {
                    products,
                    sortingOptions,
                    refinementsData,
                    categories,
                    numTotalResults: response.total_num_results,
                    page: this.#currentPage,
                    nextPage: this.#nextPage,
                    pages: this.#pages,
                    isCategorySearch: false,
                    refinementURLBuilder: ConstructorRefinementURLBuilder
                };
            }

            const redirectUrl = response?.redirect?.data?.url;

            if (redirectUrl) {
                let newLocation = '';

                if (redirectUrl.includes('http')) {
                    newLocation = redirectUrl;
                } else {
                    const {origin} = new URL(window.location.href);

                    const countryPath = getCountryPath();
                    newLocation = `${origin}${countryPath}${redirectUrl}`;
                }

                window.location.href = newLocation;

                return;
            }
        }

        return this.#emptyResult;
    };

    /**
     * @param {string} query - query to search
     * @param {Object} [parameters] - search parameters
     * @returns {Promise<import('../../spa/type-definitions').SearchResult>} result object
     */
    async search(query, parameters) {
        try {
            this.#setParameters(parameters);
            const requestData = {
                resultsPerPage: this.#resultsPerPage,
                page: this.#nextPage,
                filters: this.#filters,
                variationsMap: this.#variations_map
            };
            if (this.#sortBy !== null) {
                requestData.sortBy = this.#sortBy;
            }
            if (this.#sortOrder !== null) {
                requestData.sortOrder = this.#sortOrder;
            }
            const result = await this.#client.search.getSearchResults(query, requestData);
            const response = this.#handleResults(result);
            return response;
        } catch (error) {
            console.error(error);
            return this.#emptyResult;
        }
    }

    /**
     * @param {string} categoryID - category ID to search
     * @param {Object} [parameters] - browse parameters
     * @returns {Promise<import('../../spa/type-definitions').SearchResult>} result object
     */
    async browse(categoryID, parameters) {
        try {
            this.#setParameters(parameters);
            const requestData = {
                resultsPerPage: this.#resultsPerPage,
                page: this.#nextPage,
                filters: this.#filters,
                variationsMap: this.#variations_map
            };
            if (this.#sortBy !== null) {
                requestData.sortBy = this.#sortBy;
            }
            if (this.#sortOrder !== null) {
                requestData.sortOrder = this.#sortOrder;
            }
            const result = await this.#client.browse.getBrowseResults('group_id', categoryID, requestData);
            const response = this.#handleResults(result);
            response.isCategorySearch = true;

            if (
                response.isCategorySearch &&
                response.categories &&
                (response.categories[0].id.includes('cosmetics') ||
                    response.categories[0].id.includes('care-essentials'))
            ) {
                response.isCosmeticsPage = true;
            } else {
                response.isCosmeticsPage = false;
            }
            return response;
        } catch (error) {
            console.error(error);
            return this.#emptyResult;
        }
    }
}

export default new ConstructorIOSearch();
